import { useEffect, useMemo, useState } from "react";
import {
  AddProductCard,
  CategorySideMenu,
  FloatingMenuButton,
} from "../components";
import { StoreCategoryModel, StoreProductModel } from "../models";
import { useLayout } from "../context";
import { useStore } from "../hooks";
import { Col, Row } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { Element, scroller, animateScroll as scroll } from "react-scroll";
import Skeleton from "react-loading-skeleton";
import { Layout } from "../layouts";
import { isEmpty, ucwords } from "../helpers";
import React from "react";
import { FaChevronRight } from "react-icons/fa";

const MenuPage = () => {
  const [query, setQuery] = useState<string>("");

  const { setIsSidebarCollapsed, isSidebarCollapsed } = useLayout();
  const { storeCategories, storeProducts } = useStore();
  const { categoryId, productId } = useParams();

  const searchFilteredProducts = useMemo(
    () =>
      storeProducts.filter((item) =>
        item.product_name.toLowerCase().includes(query.toLowerCase()),
      ),
    [query, storeProducts],
  );

  const filteredCategories = useMemo(() => {
    if (!storeCategories?.length) return [];
    return storeCategories.filter((item) => {
      const hasProduct = searchFilteredProducts?.some(
        (product) => product.cid === item.store_category_id,
      );

      const isTopLevel = !query && item.parent_id == "0";

      return hasProduct || isTopLevel;
    });
  }, [query, searchFilteredProducts, storeCategories]);

  const defaultCategoryId = useMemo(() => {
    const firstParentCategory = filteredCategories.find(
      (category) => category.parent_id == "0",
    );

    if (!firstParentCategory) return "";

    const firstSubCategory = filteredCategories.find(
      (category) => category.parent_id == firstParentCategory.store_category_id,
    );

    return (
      firstSubCategory?.store_category_id ||
      firstParentCategory.store_category_id
    );
  }, [filteredCategories]);

  const selectedCategoryId = categoryId || defaultCategoryId;

  // The visible gap needed above a scroll target changes with the sticky
  // header CSS (breakpoint-dependent clamp() values, and parent categories
  // additionally have a `.food_item_parent_sticky` block on desktop). A
  // fixed offset only matches one breakpoint, so read the real computed
  // `top` of whichever sticky element applies to this target instead.
  const getStickyOffset = (container: HTMLElement | null) => {
    if (!container) return 0;

    const stickyHeader = container.querySelector<HTMLElement>(
      ".food_item_parent_sticky",
    );

    if (stickyHeader) {
      // .active_sticky_header only gets added once this row's scroll
      // position crosses a threshold — which is exactly what scrolling here
      // is about to do. Reading its *current* state would make the offset
      // depend on where we already are, which the correction loop is also
      // trying to change: a feedback loop that bounces the scroll position
      // back and forth. Force-read the top it will have once active
      // instead, so the offset stays the same no matter when it's read.
      const wasActive = stickyHeader.classList.contains("active_sticky_header");
      if (!wasActive) stickyHeader.classList.add("active_sticky_header");
      const top = parseFloat(getComputedStyle(stickyHeader).top);
      if (!wasActive) stickyHeader.classList.remove("active_sticky_header");
      return Number.isNaN(top) ? 0 : top;
    }

    // No parent sticky block on this target — fall back to the heading,
    // whose sticky top (if any) is purely CSS/viewport-driven, not
    // scroll-position-driven, so it's stable to read directly.
    const heading = container.querySelector<HTMLElement>("h3");
    if (heading && getComputedStyle(heading).position === "sticky") {
      const top = parseFloat(getComputedStyle(heading).top);
      return Number.isNaN(top) ? 0 : top;
    }

    return 0;
  };

  // Landing here from another page, #store-menu can still be reflowing while
  // the smooth scroll below is in flight — and scrolling itself is part of
  // the cause: .food_item_parent_sticky sits `position: absolute` (no
  // layout height) until handleStickyHeader's scroll listener adds
  // .active_sticky_header once a row nears the top, which flips it to
  // `position: sticky` and makes it start occupying real height, pushing
  // everything below (including the element we just scrolled to) further
  // down. react-scroll computes the destination once and never re-measures,
  // so the animated scroll alone can't land correctly here.
  //
  // Correcting for that means firing more scroll events, which can trigger
  // .active_sticky_header on/off again and restart its own opacity/transform
  // transition — visible as a jerk right as we land. So: suppress that
  // transition for the duration of the correction (snap to the final look
  // instead of animating it), and glide the position corrections instead of
  // teleporting. Stop once two consecutive reads agree nothing moved. On an
  // already-settled page (e.g. CategorySideMenu clicks) the first read
  // already agrees, so this is a single, invisible no-op correction.
  const SETTLING_CLASS = "scroll-settling";
  const CORRECTION_DURATION = 250;

  const scrollToElement = (name: string, getOffset: () => number) => {
    const container = document.getElementById("store-menu");
    container?.classList.add(SETTLING_CLASS);

    scroller.scrollTo(name, {
      duration: 1200,
      spy: true,
      smooth: true,
      offset: getOffset(),
      containerId: "store-menu",
    });

    const readGap = () => {
      const target = document.getElementById(name);
      if (!container || !target) return null;
      return (
        target.getBoundingClientRect().top -
        container.getBoundingClientRect().top
      );
    };

    const settle = (attemptsLeft: number, lastGap: number | null) => {
      scroller.scrollTo(name, {
        duration: CORRECTION_DURATION,
        smooth: true,
        offset: getOffset(),
        containerId: "store-menu",
      });

      if (attemptsLeft <= 0) {
        container?.classList.remove(SETTLING_CLASS);
        return;
      }

      window.setTimeout(() => {
        const gap = readGap();
        if (gap !== null && (lastGap === null || Math.abs(gap - lastGap) > 2)) {
          settle(attemptsLeft - 1, gap);
        } else {
          container?.classList.remove(SETTLING_CLASS);
        }
      }, CORRECTION_DURATION + 100);
    };

    window.setTimeout(() => settle(3, null), 1250);
  };

  const handleCategoryScroll = (id: any) => {
    scrollToElement(`category-${id}`, () =>
      getStickyOffset(document.getElementById(`category-${id}`)),
    );
  };

  const handleProductScroll = (id: any) => {
    scrollToElement(`product-${id}`, () => {
      const productEl = document.getElementById(`product-${id}`);
      const categoryEl = productEl?.closest<HTMLElement>(
        ".food_item_listing_row",
      );
      return getStickyOffset(categoryEl ?? null);
    });
  };

  useEffect(() => {
    setIsSidebarCollapsed(true);
  }, [setIsSidebarCollapsed]);

  useEffect(() => {
    // Landing here from another page, the shared left sidebar is still
    // expanded on the first paint and only collapses (via Layout's own
    // effect) a render later. Scrolling before that collapse lands means
    // #store-menu reflows out from under the target mid-animation, so wait
    // for it to settle. Clicks inside the page (e.g. CategorySideMenu) call
    // handleCategoryScroll directly and never hit this effect, which is why
    // they don't show the drift.
    if (!isSidebarCollapsed) return;

    if (productId) {
      handleProductScroll(productId);
    } else if (categoryId) {
      handleCategoryScroll(categoryId);
    } else {
      scroll.scrollToTop();
    }
  }, [categoryId, productId, isSidebarCollapsed]);

  useEffect(() => {
    const scrollContainer = document.getElementById("store-menu");

    const handleStickyHeader = () => {
      const sections = document.querySelectorAll(".food_item_listing_row");

      sections.forEach((section: any) => {
        const stickyHeader = section.querySelector(".food_item_parent_sticky");

        if (!stickyHeader) return;

        const rect = section.getBoundingClientRect();

        if (rect.top <= 80) {
          stickyHeader.classList.add("active_sticky_header");
        } else {
          stickyHeader.classList.remove("active_sticky_header");
        }
      });
    };
    scrollContainer?.addEventListener("scroll", handleStickyHeader);

    handleStickyHeader();

    return () => {
      scrollContainer?.removeEventListener("scroll", handleStickyHeader);
    };
  }, []);

  const renderStickyHeader = (category: StoreCategoryModel) => (
    <div className="food_item_parent_sticky">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        {/* LEFT */}
        <div>
          <div className="subcategory_breadcrumb">
            <span className="text-primary">
              {" "}
              Home <FaChevronRight size={12} />{" "}
            </span>
            <span> Menu </span>
            <FaChevronRight size={12} />
            <span>{ucwords(category.category_name)}</span>
          </div>
          <h3 className="food_item_subtitle mb-1">
            {ucwords(category.category_name)}
          </h3>
          {/* <p className="subcategory_desc mb-0">
            Freshly prepared dishes crafted with premium ingredients and bold
            flavors.
          </p> */}
        </div>

        {/* RIGHT */}
        <div className="subcategory_header_actions">
          <div className="subcategory_item_count">
            <span>🛍️</span>
            <span>
              {
                searchFilteredProducts.filter(
                  (p) => p.cid === category.store_category_id,
                ).length
              }{" "}
              Items
            </span>
          </div>

          {/* <button className="filters_btn">
            <Filter size={16} />
            Filters
          </button> */}
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      {/* <ScrollableTabs tabs={storeMenu?.category} /> */}
      <div className="menu_page_header menu-flow">
        <div className="menu_page_header d-flex justify-content-start align-items-start">
          <CategorySideMenu
            selectedCategory={selectedCategoryId}
            categories={filteredCategories}
            handleSearch={(e: any) => setQuery(e.target.value)}
            handleScroll={handleCategoryScroll}
          />

          <div className="col d-flex justify-content-start menu_content flex-column">
            <div
              id="store-menu"
              className="menu-scroll-area position-relative overflow-y-auto food_item_listing_main col-md-12 px-4 py-2 px-lg-5 py-lg-3"
            >
              {/* Empty search result */}
              {query && searchFilteredProducts.length === 0 && (
                <div className="text-center fs-5">
                  No product found matching <b>"{query}"</b>
                </div>
              )}

              {/* Categories & Products */}
              {filteredCategories
                .filter((category) => category.parent_id == "0") // Parent categories only
                .map((parentCategory) => {
                  const subCategories = filteredCategories.filter(
                    (category) =>
                      category.parent_id == parentCategory.store_category_id,
                  );

                  // Function to render products for a given category
                  const renderProducts = (category: StoreCategoryModel) => (
                    <Row>
                      {searchFilteredProducts
                        ?.filter(
                          (product: StoreProductModel) =>
                            product.cid === category.store_category_id,
                        )
                        .map((product: StoreProductModel) => (
                          <Col
                            sm={12}
                            md={6}
                            key={product.id}
                            className="d-flex flex-column"
                          >
                            <Element
                              name={`product-${product.id}`}
                              id={`product-${product.id}`}
                              className="d-flex flex-column flex-grow-1"
                            >
                              <AddProductCard
                                product={product}
                                category={category}
                              />
                              <div className="outline-sm"></div>
                            </Element>
                          </Col>
                        ))}
                    </Row>
                  );

                  // Subcategories exist → render them
                  if (subCategories.length > 0) {
                    return (
                      <React.Fragment key={parentCategory.store_category_id}>
                        {/* Parent category heading */}
                        <Element
                          name={`category-${parentCategory.store_category_id}`}
                          id={`category-${parentCategory.store_category_id}`}
                          key={`parent-${parentCategory.store_category_id}`}
                        >
                          <div className="food_item_listing_row mb-0">
                            <h3>{ucwords(parentCategory.category_name)}</h3>
                            {/* Sticky Header */}
                            {renderStickyHeader(parentCategory)}
                          </div>
                        </Element>

                        {/* Subcategories */}
                        {subCategories.map((subCategory) => (
                          <Element
                            name={`category-${subCategory.store_category_id}`}
                            id={`category-${subCategory.store_category_id}`}
                            key={`sub-${subCategory.store_category_id}`}
                          >
                            <div className="food_item_listing_row">
                              <h3 className="food_item_subtitle">
                                {ucwords(subCategory.category_name)}
                              </h3>
                              {renderProducts(subCategory)}
                            </div>
                          </Element>
                        ))}
                      </React.Fragment>
                    );
                  }

                  // No subcategories → render parent
                  return (
                    <Element
                      name={`category-${parentCategory.store_category_id}`}
                      id={`category-${parentCategory.store_category_id}`}
                      key={parentCategory.store_category_id}
                    >
                      <div className="food_item_listing_row">
                        <h3>{ucwords(parentCategory.category_name)}</h3>
                        {/* Sticky Header */}
                        {renderStickyHeader(parentCategory)}
                        {renderProducts(parentCategory)}
                      </div>
                    </Element>
                  );
                }) || (
                <Row>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item) => (
                    <Col sm={12} md={6} key={item}>
                      <Skeleton height={100} className="mb-3" />
                    </Col>
                  ))}
                </Row>
              )}

              {!isEmpty(filteredCategories) && (
                <FloatingMenuButton
                  categories={filteredCategories}
                  onSelect={handleCategoryScroll}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MenuPage;
